import { useEffect, useRef, useState } from 'react';
import { competitionsService } from '../../services/competitions';
import type { EntryOptions } from '../../types/trophy';

/** What the class is worked out from; null while the form cannot ask yet. */
export interface EntryOptionsQuery {
  birthDate: string | null;
  gender: 'male' | 'female' | null;
  /** A class to keep as it is rather than work out (a squad's row the desk sorted). */
  categoryId?: string | null;
}

/** Where the answer is read from: the public endpoint, or the desk's own. */
export type EntryOptionsFetcher = (
  slug: string,
  details: { birth_date: string | null; gender: 'male' | 'female' | null; category_id: string | null }
) => Promise<EntryOptions>;

/** The public `entry_options`. Module-level, so it is the same function on every render. */
const publicEntryOptions: EntryOptionsFetcher = (slug, details) => competitionsService.entryOptions(slug, details);

/**
 * The category and rounds a gender and a date of birth lead to at a stage,
 * read from the public `entry_options` — for the forms with nobody signed in to
 * read a profile of: a squad's team manager entering an athlete, and the guided
 * sign-up of a parent. Read again whenever a value changes; only the latest
 * answer counts, and the last good one stays on screen while the next loads.
 *
 * `slug` null (a closed dialog) or `query` null (a value still missing) asks
 * nothing and clears what was there. The error is the server's words, kept in
 * state rather than turned into a toast, so no translator ends up among the
 * effect's dependencies.
 *
 * `fetchOptions` is where to ask: the public endpoint by default; the panel's
 * entry form passes the desk's own, which answers before the competition is
 * public. It must be a stable function (module-level), or every render asks
 * again.
 */
export function useEntryOptions(
  slug: string | null,
  query: EntryOptionsQuery | null,
  fetchOptions: EntryOptionsFetcher = publicEntryOptions
) {
  const [options, setOptions] = useState<EntryOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const ready = !!slug && !!query;
  const birthDate = query?.birthDate ?? null;
  const gender = query?.gender ?? null;
  const categoryId = query?.categoryId ?? null;

  useEffect(() => {
    const request = ++latestRequest.current;
    if (!ready || !slug) {
      setOptions(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    fetchOptions(slug, { birth_date: birthDate, gender, category_id: categoryId })
      .then((data) => {
        if (request !== latestRequest.current) return;
        setOptions(data);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (request !== latestRequest.current) return;
        setError(caught instanceof Error ? caught.message : '');
      })
      .finally(() => {
        if (request === latestRequest.current) setLoading(false);
      });
  }, [ready, slug, birthDate, gender, categoryId, fetchOptions]);

  return { options, loading, error };
}
